import { PayloadAction } from '@reduxjs/toolkit';
import { all, fork, call, put, select, takeLatest, take, delay } from 'redux-saga/effects';
import { IUpdateState, restoreState, updateState } from '../../../actions/product/state';
import { EntityTypes } from 'constants/entities';
import { AssetType, IAsset } from 'types/app/asset';
import { selectActionAssets } from 'core/selectors/product/action/actionAssets';
import { LoadingState } from 'types/domain/base';
import { loadAsset } from 'core/services/asset';
import { selectSceneAsset } from 'core/selectors/product/scene/sceneAsset';
import { setAssets, unloadAsset } from 'core/slices/assets';
import { IKeyArray } from 'types/IKeyArray';
import { selectFeedNext } from 'core/selectors/product/action/actionNext';
import { IActionFeedItem } from 'core/selectors/product/action/actionFeed';
import { RootState } from 'types';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';
import { loadingState } from 'core/slices/player';
import { selectAction } from 'core/selectors/product/action/action';
import { IActionBasic } from 'types/domain/action';
import { IStateReducer } from 'core/reducers/state';
import { waitForPageTransition } from '../progress/page';

export interface ILoader {
  [assetId: string]: any;
}

export const loaders = {};

function* setAssetsSaga(action: PayloadAction<IKeyArray<IAsset>>) {
  const state: IStateReducer = yield select((state: RootState) => state.state);
  if (state[EntityTypes.SCENE]) yield loadSceneAsset(state[EntityTypes.SCENE], true);
}

function* assetLoadedSaga(action: any) {
  const { id } = action.meta.arg;
  const sceneAsset = yield select(selectSceneAsset);
  const loading = select((state: RootState) => state.core.player.loading.scene === LoadingState.LOADING);
  if (sceneAsset && loading && sceneAsset.id === id && sceneAsset.type !== AssetType.VIDEO)
    yield put(yield call(loadingState, { scene: LoadingState.LOADED }));
}

function* assetFailedSaga(action: any) {
  const asset: IAsset = yield select((state: RootState) => state.core.assets[action.meta.arg.id]);

  if (asset.attempts < 5) {
    yield delay(100);
    yield put(yield call(loadAsset, asset));
  }
}

function* restoreStateSaga(action: PayloadAction<IUpdateState[]>) {
  const update = action.payload.find(update => update.type === EntityTypes.ACTION);

  if (update) yield loadActionAssets();
}

function* updateStateSaga(action: PayloadAction<Partial<IUpdateState>>) {
  switch (action.payload.type) {
    case EntityTypes.ACTION:
      yield loadActionAssets();
      break;
    case EntityTypes.SCENE:
      yield loadSceneAsset(action.payload.id, true);
      break;
    case EntityTypes.VERIFICATION_REQUEST:
      const mode: Mode = yield select(selectSessionMode);
      const next: IActionFeedItem = yield select(selectFeedNext);

      const nextAction: IActionBasic = yield select((state: RootState) =>
        //@ts-ignore
        next?.actionId ? selectAction(state, next.actionId) : null,
      );
      if (next && nextAction?.scene && mode === Mode.LEARNING) yield loadSceneAsset(nextAction.sceneId);
      break;
  }
}

function* loadActionAssets() {
  const mode: Mode = yield select(selectSessionMode);
  const nextItem: IActionFeedItem = yield select(selectFeedNext);

  const nextAction: IActionBasic = yield select((state: RootState) =>
    //@ts-ignore
    nextItem?.actionId ? selectAction(state, nextItem.actionId) : null,
  );
  const actionAssets: IAsset[] = yield select(selectActionAssets);
  const actions = yield all(
    actionAssets
      .filter(asset => ![LoadingState.LOADED, LoadingState.LOADING].includes(asset.loading))
      .map(asset => call(loadAsset, asset)),
  );
  yield all(actions.map(put));

  const action: IActionBasic = yield select(selectAction);

  if (action?.voiceSceneId) {
    yield loadSceneAsset(action.voiceSceneId);
  }

  if (nextAction && nextAction?.scene && mode !== Mode.ADMIN) yield loadSceneAsset(nextAction.sceneId);
  if (nextAction && nextAction?.voiceSceneId && mode !== Mode.ADMIN) {
    yield loadSceneAsset(nextAction.voiceSceneId);
  }
}

export function* loadSceneAsset(sceneId: number, current?: boolean) {
  //console.log('loadSceneAsset', sceneId, current);
  // const validate: Validate = yield select((state: RootState) => state.session.validate);
  //if (validate.state !== ValidateState.VALIDATED) yield take(validationRequest.fulfilled);
  yield waitForPageTransition();
  //@ts-ignore
  let sceneAsset: IAsset = yield select((state: RootState) => selectSceneAsset(state, sceneId));

  while (!sceneAsset) {
    yield take(setAssets.type);
    //@ts-ignore
    sceneAsset = yield select((state: RootState) => selectSceneAsset(state, sceneId));
  }

  if (sceneAsset && ![LoadingState.LOADED, LoadingState.LOADING].includes(sceneAsset.loading)) {
    yield put(yield call(loadAsset, sceneAsset));

    if (current) yield put(yield call(loadingState, { scene: true }));
  } else if (sceneAsset.type !== AssetType.VIDEO && sceneAsset.loading === LoadingState.LOADED)
    yield put(yield call(loadingState, { scene: LoadingState.LOADED }));
}

function* unloadAssetSaga(action: PayloadAction<string>) {
  const asset: IAsset = yield select((state: RootState) => state.core.assets[action.payload]);

  if (!loaders[asset.id]) return;

  switch (asset.type) {
    case AssetType.VIDEO:
      if (loaders[asset.id].isReady()) {
        yield call([loaders[asset.id], loaders[asset.id].pause]);
        yield call([loaders[asset.id], loaders[asset.id].destroy]);
      }
      break;
    case AssetType.IMAGE:
      if (loaders[asset.id].asset) yield call([loaders[asset.id].asset, loaders[asset.id].asset.dispose]);
      break;
  }
}

function* watchAssetLoaded() {
  yield takeLatest(loadAsset.fulfilled, assetLoadedSaga);
}

function* watchAssetFailed() {
  yield takeLatest(loadAsset.rejected, assetFailedSaga);
}

function* watchSetAssets() {
  yield takeLatest(setAssets.type, setAssetsSaga);
}

function* watchRestoreState() {
  yield takeLatest(restoreState.type, restoreStateSaga);
}

function* watchUpdateState() {
  yield takeLatest(updateState.type, updateStateSaga);
}

function* watchUnloadAsset() {
  yield takeLatest(unloadAsset, unloadAssetSaga);
}

export function* loadSaga() {
  yield all([
    fork(watchUpdateState),
    fork(watchRestoreState),
    fork(watchSetAssets),
    fork(watchAssetLoaded),
    fork(watchUnloadAsset),
    fork(watchAssetFailed),
  ]);
}
